import React, { createContext, useEffect, useRef, useState } from 'react';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import styled from 'styled-components';

import { getScrollBarWidth } from '../utils';

import { backgrounds, cardBackgrounds, cardImages } from '../constants';

const Background = styled.img`
  position: absolute;
  opacity: 0;
  z-index: -1;
  pointer-events: none;
`;

const PRELOAD_IMAGES = backgrounds.concat(cardImages).concat(cardBackgrounds);

function removeLoader() {
  const preloader = document.getElementById('preloader');

  if (preloader) preloader.remove();

  document.querySelector('body').classList.remove('preloader_ready');
}

export const WIDTH = {
  DESKTOP: 1440 - 108,
  MOBILE: 390,
};

export const HEIGHT = {
  DESKTOP: 797,
  MOBILE: 664,
};

const initialBounds = {
  scale: 1,
  width: 0,
  height: 0,
  scaledBy: 'width',
  left: 0,
  top: 0,
};

export const BoundsContext = createContext(initialBounds);

export function ContextWrapper({ children }) {
  const mounted = useRef(false);
  const timeoutId = useRef();
  const imagesToLoad = useRef({});
  const scrollbarWidth = useRef(0);
  const breakpoints = useBreakpoint();
  const [loaded, setLoaded] = useState(false);
  const [container, setContainer] = useState(initialBounds);
  const [windowBounds, setWindowBounds] = useState({ width: 0, height: 0 });

  function updateBounds() {
    if (!mounted.current) return;

    setWindowBounds({ width: window.innerWidth, height: window.innerHeight });
  }

  function onResize() {
    if (timeoutId.current) clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(updateBounds, 20);
  }

  function cleanup() {
    mounted.current = false;
    if (timeoutId.current) clearTimeout(timeoutId.current);
    window.removeEventListener('resize', onResize);
  }

  useEffect(
    () => {
      if (typeof window === 'undefined') return undefined;

      mounted.current = true;

      scrollbarWidth.current = getScrollBarWidth();

      onResize({ target: window });
      window.addEventListener('resize', onResize);

      if (!window.location.hash) {
        document.documentElement.scrollTop = 0;
      }

      return cleanup;
    },
    [],
  );

  useEffect(
    () => {
      const screenSize = breakpoints.lg ? 'DESKTOP' : 'MOBILE';
      const shift = (breakpoints.lg ? 108 : 0) + scrollbarWidth.current;
      const newContainer = {
        width: WIDTH[screenSize],
        height: HEIGHT[screenSize],
      };
      const windowAspect = (windowBounds.width - shift) / windowBounds.height;
      const containerAspect = newContainer.width / newContainer.height;

      if (windowAspect < containerAspect) {
        newContainer.scale = (windowBounds.width - shift) / newContainer.width;
        newContainer.scaledBy = 'width';
        newContainer.fullWidth = newContainer.width;
        newContainer.fullHeight = windowBounds.height / newContainer.scale;
        newContainer.top = (newContainer.fullHeight - newContainer.height) / 2;
        newContainer.left = 0;
      } else {
        newContainer.scale = windowBounds.height / newContainer.height;
        newContainer.scaledBy = 'height';
        newContainer.fullWidth = (windowBounds.width - shift) / newContainer.scale;
        newContainer.fullHeight = newContainer.height;
        newContainer.top = 0;
        newContainer.left = (newContainer.fullWidth - newContainer.width) / 2;
      }

      setContainer(newContainer);
    },
    [windowBounds],
  );

  function reduceImages(result, src) {
    const waitingForLoad = imagesToLoad.current[src];
    return result || waitingForLoad;
  }

  function onImageLoad(key) {
    return function handler() {
      imagesToLoad.current[key] = false;

      if (!Object.keys(imagesToLoad.current).reduce(reduceImages, false)) {
        const body = document.querySelector('body');
        body.classList.remove('preloader_active');
        body.classList.add('preloader_ready');
        setTimeout(removeLoader, 500);
        setLoaded(true);
      }
    };
  }

  function renderBackground(item) {
    if (imagesToLoad.current[item] === undefined) {
      imagesToLoad.current[item] = true;
    }

    return (
      <Background
        key={item}
        src={item}
        alt="Background"
        onLoad={onImageLoad(item)}
        onError={onImageLoad(item)}
      />
    );
  }

  return (
    <BoundsContext.Provider value={container}>
      {!loaded && PRELOAD_IMAGES.map(renderBackground)}
      {children}
    </BoundsContext.Provider>
  );
}
